import { useRouter, type RouteLocationNormalized } from 'vue-router'
import { usePageHistoryStore, PageType } from '@/modules/search/stores/pageHistory'
// Import the actual stores to get real data
import { useCasesStore } from '@/stores/cases'
import { useKnowledgeStore } from '@/modules/knowledge/stores/knowledgeStore'
import { useUsersStore } from '@/stores/users'
import { useTasksStore } from '@/stores/tasks'
import { stripHtmlTags } from '@/utils/textFormatting'

export function usePageHistoryTracker() {
  const router = useRouter()
  const pageHistoryStore = usePageHistoryStore()
  
  // Get store instances
  const casesStore = useCasesStore()
  const knowledgeStore = useKnowledgeStore()
  const usersStore = useUsersStore()
  const tasksStore = useTasksStore()

  function initializeTracking() {
    router.afterEach((to: RouteLocationNormalized, from: RouteLocationNormalized) => {
      trackPageVisit(to, from)
    })
  }

  function trackPageVisit(to: RouteLocationNormalized, from: RouteLocationNormalized) {
    // Skip tracking for login pages
    if (to.path === '/login' || to.name === 'Login' || to.name === 'login') {
      return
    }

    // Skip tracking for Inbox Home view
    if (to.path === '/inbox/home') {
      return
    }

    try {
      const pageType = determinePageType(to)
      
      if (pageType === null) {
        return
      }

      const replaced = from.name !== null && from.name !== undefined // Not replaced if coming from nowhere
      
      switch (pageType) {
        case PageType.CASE:
          trackCasePage(to, replaced)
          break
        case PageType.TASK:
          trackTaskPage(to, replaced)
          break
        case PageType.KB_ARTICLE:
          trackKnowledgeArticlePage(to, replaced)
          break
        case PageType.CUSTOMER:
          trackCustomerPage(to, replaced)
          break
        case PageType.LIST_VIEW:
          trackListViewPage(to, replaced)
          break
        case PageType.GENERAL:
          trackGeneralPage(to, replaced)
          break
      }
    } catch (error) {
      console.error('📝 Error tracking page visit:', error)
    }
  }

  function determinePageType(route: RouteLocationNormalized): PageType | null {
    const routeName = route.name as string
    const routePath = route.path
    
    // Case pages - check both route name and path pattern
    if (routeName === 'InboxCase' || routeName === 'inbox-case' || 
        (routePath.startsWith('/inbox/cases/') && route.params.id)) {
      return PageType.CASE
    }
    
    // Task pages  
    if (routeName === 'InboxTask' || routeName === 'inbox-task' || routeName === 'inbox-task-detail' ||
        (routePath.startsWith('/inbox/task/') && route.params.id) ||
        (routePath.startsWith('/inbox/tasks/') && route.params.id)) {
      return PageType.TASK
    }
    
    // Knowledge Base article pages
    if (routeName === 'ArticleView' || routeName === 'knowledge-article' ||
        (routePath.startsWith('/knowledge/articles/') && route.params.id)) {
      return PageType.KB_ARTICLE
    }
    
    // Customer detail pages (must check before list views)
    if (routeName === 'customer-detail' || routeName === 'location-detail' || routeName === 'contact-detail') {
      return PageType.CUSTOMER
    }
    
    // Customer list view pages (must check after detail pages)
    if (routeName === 'customers-list' || routeName === 'customers-locations' || routeName === 'customers-contacts') {
      return PageType.GENERAL // These will be handled specially as general pages with custom titles
    }
    
    // List view pages
    if (routeName === 'InboxView' || routeName === 'inbox' ||
        routeName === 'KnowledgeView' || routeName === 'knowledge' ||
        (routePath === '/inbox' || routePath === '/knowledge')) {
      return PageType.LIST_VIEW
    }
    
    // All other pages are general
    return PageType.GENERAL
  }

  function trackCasePage(route: RouteLocationNormalized, replaced: boolean) {
    const caseId = route.params.id as string
    if (!caseId) {
      console.warn('📝 No case ID found in route params')
      return
    }

    // Retry until the correct case is loaded in the store, up to ~3 seconds
    const maxAttempts = 6
    let attempts = 0
    const delays = [300, 500, 800, 1200, 1800, 3000]

    const tryTrack = () => {
      const issue = casesStore.currentIssue
      if (issue && issue.id === caseId && issue.display_name) {
        trackCasePageWithData(route, caseId, replaced)
      } else if (attempts < maxAttempts) {
        setTimeout(tryTrack, delays[attempts++])
      } else {
        // Give up and record whatever we have
        trackCasePageWithData(route, caseId, replaced)
      }
    }

    setTimeout(tryTrack, delays[attempts++])
  }

  function trackCasePageWithData(route: RouteLocationNormalized, caseId: string, replaced: boolean) {
    // Try to get case data from the current issue in the store
    let caseData = {
      refId: caseId,
      name: 'Loading...',
      status: null as string | null,
      location: undefined as string | undefined
    }

    // Check if we have the current case loaded
    if (casesStore.currentIssue && casesStore.currentIssue.id === caseId) {
      const issue = casesStore.currentIssue
      
      // Extract case reference number and name
      const refId = issue.reference_num || (issue as any).c__conference_id || issue.display_name || caseId
      const caseName = stripHtmlTags(issue.display_name || 'Unknown Case')
      
      // Extract status - only use c__status, can be null/blank
      const status = (issue as any).c__status || null
      
      // Extract location information - use location.site_name
      const locationName = issue.location?.site_name || 
                           (issue as any).c__location || 
                           (issue as any).c__name || 
                           issue.location?.name
      
      caseData = {
        refId: refId,
        name: caseName,
        status: status,
        location: locationName
      }
    } else {
      // Check if the case exists in any of the case arrays
      const allCases = [
        ...casesStore.issues,
        ...casesStore.myWorkIssues,
        ...casesStore.memberCases
      ]
      
      const foundCase = allCases.find(issue => issue.id === caseId)
      if (foundCase) {
        
        // Extract case reference number and name
        const refId = foundCase.reference_num || (foundCase as any).c__conference_id || foundCase.display_name || caseId
        const caseName = stripHtmlTags(foundCase.display_name || 'Unknown Case')
        
        // Extract status - only use c__status, can be null/blank
        const status = (foundCase as any).c__status || null
        
        // Extract location information - use location.site_name
        const locationName = foundCase.location?.site_name || 
                             (foundCase as any).c__location || 
                             (foundCase as any).c__name || 
                             foundCase.location?.name
        
        caseData = {
          refId: refId,
          name: caseName,
          status: status,
          location: locationName
        }
      } else {
        // Use fallback data with the case ID
        caseData = {
          refId: caseId,
          name: 'No Case Name',
          status: null,
          location: undefined
        }
      }
    }
    
    const pageItem = pageHistoryStore.createCasePageItem(route, caseData, replaced)
    pageHistoryStore.addPageHistoryItem(pageItem)
  }

  function trackTaskPage(route: RouteLocationNormalized, replaced: boolean) {
    const taskId = route.params.id as string
    if (!taskId) {
      console.warn('📝 No task ID found in route params')
      return
    }

    // Add a small delay to allow task data to load
    setTimeout(() => {
      trackTaskPageWithData(route, taskId, replaced)
    }, 500)
  }

  function trackTaskPageWithData(route: RouteLocationNormalized, taskId: string, replaced: boolean) {
    // Try to get task data from the tasks store
    let taskData = {
      name: `Task ${taskId}`,
      status: 'Unknown'
    }

    // Check all task arrays in the store
    const allTasks = [
      ...tasksStore.userTasks,
      ...tasksStore.teamTasks,
      ...tasksStore.allTasks
    ]

    const foundTask = allTasks.find(task => task.id === taskId)
    if (foundTask) {
      taskData = {
        name: stripHtmlTags(foundTask.lbl || foundTask.name || `Task ${taskId}`),
        status: foundTask.status || 'Unknown'
      }
    }
    
    const pageItem = pageHistoryStore.createTaskPageItem(route, taskData, replaced)
    pageHistoryStore.addPageHistoryItem(pageItem)
  }

  function trackKnowledgeArticlePage(route: RouteLocationNormalized, replaced: boolean) {
    const articleId = route.params.id as string
    if (!articleId) {
      console.warn('📝 No article ID found in route params')
      return
    }

    // Add a small delay to allow article data to load
    setTimeout(() => {
      trackKnowledgeArticlePageWithData(route, articleId, replaced)
    }, 500)
  }

  function trackKnowledgeArticlePageWithData(route: RouteLocationNormalized, articleId: string, replaced: boolean) {
    // Try to get article data from the knowledge store
    let articleData = {
      title: `Article ${articleId}`,
      status: 'Unknown',
      subtitle: undefined as string | undefined
    }

    // Check if we have the current article loaded
    if (knowledgeStore.currentArticle && knowledgeStore.currentArticle.id === articleId) {
      const article = knowledgeStore.currentArticle
      
      articleData = {
        title: stripHtmlTags(article.title || article.lbl || `Article ${articleId}`),
        status: article.c__d_status || 'Unknown',
        subtitle: article.sub_title || undefined
      }
    } else {
      // Check if the article exists in the items array
      const foundArticle = knowledgeStore.items.find(item => item.id === articleId)
      if (foundArticle) {
        articleData = {
          title: stripHtmlTags(foundArticle.title || foundArticle.lbl || `Article ${articleId}`),
          status: foundArticle.c__d_status || 'Unknown', 
          subtitle: foundArticle.sub_title || undefined
        }
      } else {
        // Check current list
        const foundInList = knowledgeStore.currentList.find(item => item.id === articleId)
        if (foundInList) {
          articleData = {
            title: stripHtmlTags(foundInList.title || foundInList.lbl || `Article ${articleId}`),
            status: foundInList.c__d_status || 'Unknown',
            subtitle: foundInList.sub_title || undefined
          }
        }
      }
    }
    
    const pageItem = pageHistoryStore.createKBArticlePageItem(route, articleData, replaced)
    pageHistoryStore.addPageHistoryItem(pageItem)
  }

  function trackCustomerPage(route: RouteLocationNormalized, replaced: boolean) {
    const customerId = route.params.id as string
    if (!customerId) {
      console.warn('📝 No customer ID found in route params')
      return
    }

    const routeName = route.name as string

    // Add delays and retry logic to wait for stores to load
    if (routeName === 'location-detail') {
      trackLocationWithRetry(route, customerId, replaced, 0)
    } else if (routeName === 'contact-detail') {
      trackContactWithRetry(route, customerId, replaced, 0)
    } else if (routeName === 'customer-detail') {
      trackCustomerWithRetry(route, customerId, replaced, 0)
    }
  }

  function trackLocationWithRetry(route: RouteLocationNormalized, locationId: string, replaced: boolean, attempt: number) {
    const maxAttempts = 5
    const delayMs = 300
    
    setTimeout(async () => {
      try {
        const { useLocationsStore } = await import('@/stores/locationsStore')
        const locationsStore = useLocationsStore()
        
        if (locationsStore.currentLocation?.id === locationId) {
          const locationName = stripHtmlTags(locationsStore.currentLocation.site_name || `Location ${locationId}`)
          
          const customerData = {
            name: locationName,
            status: ''
          }
          
          const pageItem = pageHistoryStore.createCustomerPageItem(route, customerData, replaced)
          pageHistoryStore.addPageHistoryItem(pageItem)
          return
        }
        
        // Retry if data not loaded yet
        if (attempt < maxAttempts) {
          trackLocationWithRetry(route, locationId, replaced, attempt + 1)
          return
        }
        
        // Fallback after max attempts
        const customerData = {
          name: `Location ${locationId}`,
          status: ''
        }
        
        const pageItem = pageHistoryStore.createCustomerPageItem(route, customerData, replaced)
        pageHistoryStore.addPageHistoryItem(pageItem)
      } catch (error) {
        console.error('Error tracking location page:', error)
      }
    }, attempt === 0 ? 500 : delayMs)
  }

  function trackContactWithRetry(route: RouteLocationNormalized, contactId: string, replaced: boolean, attempt: number) {
    const maxAttempts = 5
    const delayMs = 300
    
    setTimeout(async () => {
      try {
        const { useContactsStore } = await import('@/stores/contactsStore')
        const contactsStore = useContactsStore()
        
        if (contactsStore.currentContact?.id === contactId) {
          const contact = contactsStore.currentContact
          const contactName = stripHtmlTags(
            contact.full_name || 
            [contact.first_name, contact.last_name].filter(Boolean).join(' ') ||
            contact.public_name || 
            `Contact ${contactId}`
          )
          
          const customerData = {
            name: contactName,
            status: ''
          }
          
          const pageItem = pageHistoryStore.createCustomerPageItem(route, customerData, replaced)
          pageHistoryStore.addPageHistoryItem(pageItem)
          return
        }
        
        // Retry if data not loaded yet
        if (attempt < maxAttempts) {
          trackContactWithRetry(route, contactId, replaced, attempt + 1)
          return
        }
        
        // Fallback after max attempts
        const customerData = {
          name: `Contact ${contactId}`,
          status: ''
        }
        
        const pageItem = pageHistoryStore.createCustomerPageItem(route, customerData, replaced)
        pageHistoryStore.addPageHistoryItem(pageItem)
      } catch (error) {
        console.error('Error tracking contact page:', error)
      }
    }, attempt === 0 ? 500 : delayMs)
  }

  function trackCustomerWithRetry(route: RouteLocationNormalized, customerId: string, replaced: boolean, attempt: number) {
    const maxAttempts = 5
    const delayMs = 300
    
    setTimeout(async () => {
      try {
        const { useCustomersStore } = await import('@/stores/customersStore')
        const customersStore = useCustomersStore()
        
        if (customersStore.currentCustomer?.id === customerId) {
          const customer = customersStore.currentCustomer
          const customerName = stripHtmlTags(customer.name || customer.nickname || `Customer ${customerId}`)
          
          const customerData = {
            name: customerName,
            status: ''
          }
          
          const pageItem = pageHistoryStore.createCustomerPageItem(route, customerData, replaced)
          pageHistoryStore.addPageHistoryItem(pageItem)
          return
        }
        
        // Retry if data not loaded yet
        if (attempt < maxAttempts) {
          trackCustomerWithRetry(route, customerId, replaced, attempt + 1)
          return
        }
        
        // Fallback after max attempts
        const customerData = {
          name: `Customer ${customerId}`,
          status: ''
        }
        
        const pageItem = pageHistoryStore.createCustomerPageItem(route, customerData, replaced)
        pageHistoryStore.addPageHistoryItem(pageItem)
      } catch (error) {
        console.error('Error tracking customer page:', error)
      }
    }, attempt === 0 ? 500 : delayMs)
  }

  function trackAIAgentConfigWithRetry(route: RouteLocationNormalized, agentId: string, replaced: boolean, attempt: number) {
    const maxAttempts = 5
    const delayMs = 300
    
    setTimeout(async () => {
      try {
        const { useAIStudioStore } = await import('@/modules/AIStudio/stores/aiStudioStore')
        const aiStudioStore = useAIStudioStore()
        
        // Check if the agent config is loaded and matches the current agent ID
        if (aiStudioStore.agentId === agentId && aiStudioStore.config?.metadata?.name) {
          const agentName = stripHtmlTags(aiStudioStore.config.metadata.name)
          const title = `AI Studio - ${agentName} Configuration`
          
          const generalData = {
            title,
            section: 'AI Studio'
          }
          
          const pageItem = pageHistoryStore.createGeneralPageItem(route, generalData, replaced)
          pageHistoryStore.addPageHistoryItem(pageItem)
          return
        }
        
        // Retry if data not loaded yet
        if (attempt < maxAttempts) {
          trackAIAgentConfigWithRetry(route, agentId, replaced, attempt + 1)
          return
        }
        
        // Fallback after max attempts - use agent ID
        const title = `AI Studio - Agent ${agentId} Config`
        const generalData = {
          title,
          section: 'AI Studio'
        }
        
        const pageItem = pageHistoryStore.createGeneralPageItem(route, generalData, replaced)
        pageHistoryStore.addPageHistoryItem(pageItem)
      } catch (error) {
        console.error('Error tracking AI agent config page:', error)
        // Fallback on error
        const title = `AI Studio - Agent ${agentId} Config`
        const generalData = {
          title,
          section: 'AI Studio'
        }
        
        const pageItem = pageHistoryStore.createGeneralPageItem(route, generalData, replaced)
        pageHistoryStore.addPageHistoryItem(pageItem)
      }
    }, attempt === 0 ? 500 : delayMs)
  }

  function trackListViewPage(route: RouteLocationNormalized, replaced: boolean) {
    // Determine view type from route name
    const routeName = route.name as string
    let viewType = 'Items'

    if (routeName === 'InboxView' || routeName === 'inbox') {
      // Check if it's tasks or cases from the query params
      if (route.query.object === 'tasks') {
        viewType = 'Tasks List'
      } else {
        viewType = 'Cases'
      }
    } else if (routeName === 'ContactsListView' || routeName === 'contacts') {
      viewType = 'Contacts'
    } else if (routeName === 'CustomersListView' || routeName === 'customers') {
      viewType = 'Customers'
    } else if (routeName === 'KnowledgeView' || routeName === 'Knowledge' || routeName === 'knowledge') {
      // Determine if it's articles or templates based on the knowledge store
      const contentType = knowledgeStore.selectedContentType || 'articles'
      viewType = contentType === 'templates' ? 'Templates' : 'Articles'
    }

    const listViewData = {
      filters: {},
      query: '',
      page: 1,
      totalResults: 0,
      viewType
    }
    
    const pageItem = pageHistoryStore.createListViewPageItem(route, listViewData, replaced)
    pageHistoryStore.addPageHistoryItem(pageItem)
  }

  function trackGeneralPage(route: RouteLocationNormalized, replaced: boolean) {
    // Determine title and section from route
    let title = 'Unknown Page'
    let section = 'General'

    const routeName = route.name as string
    const routePath = route.path
    
    // Handle Customer list pages specially
    if (routeName === 'customers-list') {
      title = 'Customers List'
      section = 'Customers'
    } else if (routeName === 'customers-locations') {
      title = 'Customer Locations List'
      section = 'Customers'
    } else if (routeName === 'customers-contacts') {
      title = 'Contacts List'
      section = 'Customers'
    }
    // Handle Settings pages specially
    else if (routeName === 'settings-section' && route.params.section) {
      const sectionParam = route.params.section as string
      // Convert kebab-case to Title Case
      const pageName = sectionParam
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      
      title = `Settings - ${pageName}`
      section = 'Settings'
    }
    // Handle AI Studio pages specially
    else if (routePath.startsWith('/aistudio')) {
      if (routeName === 'aistudio-agents') {
        title = 'AI Studio - Agents'
        section = 'AI Studio'
      } else if (routeName === 'aistudio-branding') {
        title = 'AI Studio - Branding'
        section = 'AI Studio'
      } else if (routeName === 'aistudio-performance') {
        title = 'AI Studio - Performance'
        section = 'AI Studio'
      } else if (routeName === 'aistudio-agent-config' && route.params.id) {
        // For agent config pages, track with retry to get agent name
        const agentId = route.params.id as string
        trackAIAgentConfigWithRetry(route, agentId, replaced, 0)
        return // Early return since we're handling this asynchronously
      } else {
        title = 'AI Studio'
        section = 'AI Studio'
      }
    }
    // Map other common route names to readable titles
    else {
      const routeTitleMap: Record<string, { title: string; section: string }> = {
        'Home': { title: 'Dashboard', section: 'Home' },
        'HomeView': { title: 'Dashboard', section: 'Home' },
        'home': { title: 'Dashboard', section: 'Home' },
        'AnalyticsView': { title: 'Analytics', section: 'Analytics' },
        'Analytics': { title: 'Analytics', section: 'Analytics' },
        'analytics': { title: 'Analytics', section: 'Analytics' },
        'SettingsView': { title: 'Settings', section: 'Settings' },
        'Settings': { title: 'Settings', section: 'Settings' },
        'settings': { title: 'Settings', section: 'Settings' },
        'UserProfile': { title: 'User Profile', section: 'Profile' },
        'Profile': { title: 'User Profile', section: 'Profile' },
        'profile': { title: 'User Profile', section: 'Profile' },
        'JourneysView': { title: 'Journeys', section: 'Journeys' },
        'Journeys': { title: 'Journeys', section: 'Journeys' },
        'journeys': { title: 'Journeys', section: 'Journeys' }
      }

      if (routeTitleMap[routeName]) {
        title = routeTitleMap[routeName].title
        section = routeTitleMap[routeName].section
      } else if (routeName) {
        // Convert camelCase/PascalCase to readable title
        title = routeName
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .replace(/View$/, '')
          .trim()
        section = 'General'
      }
    }

    const generalData = {
      title,
      section
    }
    
    const pageItem = pageHistoryStore.createGeneralPageItem(route, generalData, replaced)
    pageHistoryStore.addPageHistoryItem(pageItem)
  }

  return {
    initializeTracking
  }
} 